import { useState, useMemo, useCallback, useEffect } from 'react'
import type { CSSProperties, JSX } from 'react'

import { AssistedBillingNotice } from '@buffer-mono/app-shell/src/components/AssistedBillingNotice'
import { SubscriptionUpdateModal } from '@buffer-mono/app-shell/src/components/Modal/modals/SubscriptionUpdate'
import type { SubscriptionUpdate } from '@buffer-mono/app-shell/src/components/Modal/modals/SubscriptionUpdate'
import { PlanOverrideConfirmationDialog } from '@buffer-mono/app-shell/src/components/PlanOverrideConfirmationDialog'
import { CHANNEL_LIMITS } from '@buffer-mono/app-shell/src/components/PlanSelector/constants'
import { useGetCustomInvoice } from '@buffer-mono/app-shell/src/components/PlanSelector/hooks/useGetCustomInvoice'
import { usePlanSelectorQuery } from '@buffer-mono/app-shell/src/components/PlanSelector/hooks/usePlanSelectorQuery'
import { useUpdateBillingSubscriptionPlan } from '@buffer-mono/app-shell/src/components/PlanSelector/hooks/useUpdateBillingSubscriptionPlan'
import {
  OBPlanIdValues,
  BillingIntervalValues,
  UpdateType,
  UpdatePlanPhase,
} from '@buffer-mono/app-shell/src/components/PlanSelector/types'
import { getVolumeDiscountMessage } from '@buffer-mono/app-shell/src/components/PlanSelector/utils/dataTransformers'
import type { ExtendedPlanSelectorData } from '@buffer-mono/app-shell/src/components/PlanSelector/utils/dataTransformers'
import { applyDiscountsToCalculation } from '@buffer-mono/app-shell/src/components/PlanSelector/utils/discountCalculations'
import {
  formatPrice,
  calculateTierPrice,
} from '@buffer-mono/app-shell/src/components/PlanSelector/utils/pricingCalculations'
import { useScaRetryState } from '@buffer-mono/app-shell/src/hooks/useScaRetryState'
import { useStripePaymentConfirmation } from '@buffer-mono/app-shell/src/hooks/useStripePaymentConfirmation'
import {
  Dialog,
  Flex,
  Card,
  Text,
  Button,
  CounterInput,
  CheckIcon,
  toast,
  Skeleton,
  Badge,
  useDebounceCallback,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useOrganizationId, useAccount } from '~publish/contexts/AccountContext'
import type { OBBilling } from '~publish/contexts/AccountContext'
import { getEntriesWithKeys } from '~publish/helpers/getEntriesWithKeys'

import styles from './AddOrRemoveChannelsDialog.module.css'

/**
 * A billing subscription's interval (`'month'` | `'year'`), derived from the
 * `billing { subscription { interval } }` selection on the account query.
 */
type BillingInterval = NonNullable<OBBilling['subscription']>['interval']

/**
 * Formats a discount object into badge display text
 * @param discount - AmountDiscount or PercentDiscount from query data
 * @returns Formatted discount text (e.g., "50% off", "$10.00 off")
 */
const formatDiscountBadge = (discount: {
  __typename?: 'PercentDiscount' | 'AmountDiscount'
  percentOff?: number
  amountOff?: number
  currency?: string
}): string => {
  if (discount.__typename === 'PercentDiscount' && discount.percentOff) {
    return `${discount.percentOff}% off`
  }
  if (discount.__typename === 'AmountDiscount' && discount.amountOff) {
    // Stripe amounts are in cents, convert to dollars
    return `$${(discount.amountOff / 100).toFixed(2)} off`
  }
  return ''
}

/**
 * Formats a plan string for tracking (matches legacy modal pattern)
 * Converts "essentials month" to "essentialsMonth"
 * @param str - Plan ID and interval (e.g., "essentials month")
 * @returns Formatted CTA string (e.g., "essentialsMonth")
 */
const formatCTAString = (str: string): string => {
  const result = str
    .split(/\s+/)
    .map((word: string) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join('')
  return `${result.charAt(0).toLowerCase()}${result.slice(1)}`
}

type InputWidthStyle = CSSProperties & { '--input-width': string }

const getInputWidthStyle = (channelCount: number): InputWidthStyle => ({
  '--input-width': `${channelCount.toString().length}ch`,
})

interface AddOrRemoveChannelsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * CTA identifier for tracking where the dialog was opened from
   * (e.g., 'billing-plansCard-addRemoveChannels-1')
   */
  cta?: string
}

export function AddOrRemoveChannelsDialog({
  open,
  onOpenChange,
  cta = 'unknown',
}: AddOrRemoveChannelsDialogProps): JSX.Element {
  const organizationId = useOrganizationId()
  const { account } = useAccount()

  // Use raw query data to get ALL available plans (not filtered by billing cycle)
  const { data: queryData, refetch } = usePlanSelectorQuery()

  // Use findCurrentPlan utility to correctly match plan by ID and interval

  const subscription = queryData?.subscription

  // Currency will be determined by the backend based on user's account
  // Default to USD for display purposes
  const currency = 'USD'

  const { updatePlan, loading: isUpdatingPlan } =
    useUpdateBillingSubscriptionPlan()

  // SCA payment confirmation hook
  const {
    confirmPayment,
    isConfirming,
    isFeatureEnabled: isScaEnabled,
  } = useStripePaymentConfirmation()

  const {
    getCustomInvoice,
    data: customInvoiceData,
    loading: isLoadingDynamicPrices,
  } = useGetCustomInvoice()

  const { dueNow } = customInvoiceData ?? {
    dueNow: 0,
  }

  const currentPlanQuantity = subscription?.quantity ?? 1

  // State management
  const [channelCount, setChannelCount] = useState(currentPlanQuantity)
  const safeChannelCount =
    typeof channelCount === 'number' && !isNaN(channelCount)
      ? channelCount
      : currentPlanQuantity
  const [showOverrideDialog, setShowOverrideDialog] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [subscriptionUpdateData, setSubscriptionUpdateData] =
    useState<SubscriptionUpdate | null>(null)
  const [updatePlanPhase, setUpdatePlanPhase] = useState<UpdatePlanPhase>(
    UpdatePlanPhase.Idle,
  )

  // Payment retry state using centralized hook (VLG-394)
  const {
    retryState,
    saveRetryData,
    clearRetryData,
    hasParametersChanged,
    canRetry,
  } = useScaRetryState<string, BillingInterval>()

  // Sync the CounterInput default value when the subscription plan changes
  // This ensures the input resets to the new plan quantity after successful updates
  useEffect(() => {
    if (open) {
      setChannelCount(currentPlanQuantity)
    }
  }, [currentPlanQuantity, open])

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setChannelCount(currentPlanQuantity)
      setShowOverrideDialog(false)
      setUpdatePlanPhase(UpdatePlanPhase.Idle)
      // Clear retry state (VLG-394)
      clearRetryData()
    }
  }, [open, currentPlanQuantity, clearRetryData])

  // Track modal viewed event (matches legacy QuantityUpdate modal tracking)
  useEffect(() => {
    if (!open || !account?.currentOrganization?.id || !subscription) return

    BufferTracker.quantityUpdateModalViewed({
      organizationId: account.currentOrganization.id,
      currentPlan: formatCTAString(
        `${subscription.plan.id} ${subscription.interval}`,
      ),
      cta: `publish-${cta}`,
      ctaButton: 'confimAndPay', // Match legacy typo exactly
      ctaView: 'addOrRemoveChannelsDialog',
      ctaLocation: 'addOrRemoveChannelsDialog',
    })
  }, [open, account, subscription, cta])

  const hasChannelCountChanged = safeChannelCount !== currentPlanQuantity
  const isDowngradingAction = customInvoiceData?.isScheduledChange ?? false

  // Check if there's a scheduled update
  const displayScheduledUpdate = subscription?.scheduledUpdate
    ? new Date(String(subscription.scheduledUpdate.effectiveDate)).getTime() >=
      new Date(new Date().setHours(0, 0, 0, 0)).getTime()
      ? subscription.scheduledUpdate
      : null
    : null

  // Ensure tiers are sorted by upperBound for reliable tier detection
  // Use actual subscription pricing (source of truth for what user is currently paying)
  const sortedPricingTiers = useMemo(
    () =>
      [...(subscription?.plan.pricing ?? [])].toSorted(
        (a, b) => a.upperBound - b.upperBound,
      ),
    [subscription?.plan.pricing],
  )

  // ═══════════════════════════════════════════════════════
  // PRICING TYPE DETECTION (VLG-384)
  // ═══════════════════════════════════════════════════════
  // Detect if this is tiered pricing (volume discounts) vs non-tiered (flat rate)
  // Tiered plans have >2 pricing tiers with different unitPrice values
  const isTieredPricing = useMemo(() => {
    const isFreeUser = subscription?.plan.id === OBPlanIdValues.free

    // Free users always see tiered plans (they can only upgrade to tiered)
    if (isFreeUser) return true

    // Tiered pricing: >2 tiers indicates volume discount structure
    // Non-tiered: 1-2 tiers indicates flat rate or Agency pricing
    return sortedPricingTiers.length > 2
  }, [subscription?.plan.id, sortedPricingTiers])

  // Agency plans have special handling: 10 channel minimum, flatFee pricing
  const isAgencyPlan = subscription?.plan.id === OBPlanIdValues.agency

  // Dynamic minimum channel count based on plan type
  const minChannelCount = useMemo(() => {
    // Agency plans require 10 channels minimum
    if (isAgencyPlan) {
      return 10
    }
    return CHANNEL_LIMITS.min // Default: 1
  }, [isAgencyPlan])

  // Use the discounts data from queryData
  const discounts = useMemo(
    () => queryData?.discounts || [],
    [queryData?.discounts],
  )

  // Calculate the base price using tiered pricing
  const baseCalculation = useMemo(
    () => calculateTierPrice(sortedPricingTiers, safeChannelCount),
    [sortedPricingTiers, safeChannelCount],
  )

  // Apply discounts to get the final calculation
  const discountedCalculation = useMemo(
    () => applyDiscountsToCalculation(baseCalculation, discounts),
    [baseCalculation, discounts],
  )

  const volumeDiscountMessage = getVolumeDiscountMessage(
    safeChannelCount,
    sortedPricingTiers,
  )

  // Get the total price with discounts applied
  const totalFuturePlanPrice = discountedCalculation.totalPrice
  const dueNowAmount = dueNow ?? 0

  const totalDueAmountWithCurrency = formatPrice(totalFuturePlanPrice, currency)

  const totalDueNowAmountWithCurrency = formatPrice(dueNowAmount, currency)

  const totalDueHeader = `Total: ${totalDueAmountWithCurrency}/${
    subscription?.interval ?? 'month'
  }`

  const totalDueSubHeader = isDowngradingAction
    ? `${totalDueAmountWithCurrency} will be billed on ${customInvoiceData?.nextBillingDate}`
    : `${totalDueNowAmountWithCurrency} due now · Billing is prorated for mid-cycle changes`

  const ctaButtonText = isDowngradingAction
    ? 'Confirm Plan Change'
    : 'Confirm and Pay'

  const fetchPricing = useCallback(
    async (quantity: number): Promise<void> => {
      if (!subscription || !organizationId) return

      try {
        await getCustomInvoice({
          organizationId,
          planId: subscription.plan.id,
          interval: subscription.interval,
          quantity,
        })
      } catch {
        // Silent catch for graceful degradation
      }
    },
    [getCustomInvoice, organizationId, subscription],
  )

  // Debounce pricing fetches to avoid race conditions when user rapidly changes channel count
  const debouncedFetchPricing = useDebounceCallback(fetchPricing, 300)

  const handleCountChange = useCallback(
    (value: number) => {
      // Enforce minimum channel count (e.g., 10 for agency plans)
      const clampedValue = Math.max(value, minChannelCount)
      setChannelCount(clampedValue)
      void debouncedFetchPricing(clampedValue)
    },
    [debouncedFetchPricing, minChannelCount],
  )

  const updateType =
    safeChannelCount > (subscription?.quantity ?? 1)
      ? UpdateType.ChannelIncrease
      : UpdateType.ChannelDecrease

  const handleConfirmAndPayClick = async (): Promise<void> => {
    if (!subscription) return

    try {
      // Get current parameters for comparison (VLG-394)
      const currentQuantity = safeChannelCount
      const currentPlanId = subscription.plan.id
      const currentInterval = subscription.interval

      const currentParams = {
        planId: currentPlanId,
        interval: currentInterval,
        quantity: currentQuantity,
      }

      // SCENARIO A: Pure retry - reuse client secret (VLG-394)
      if (
        isScaEnabled &&
        canRetry() &&
        !hasParametersChanged(currentParams) &&
        retryState.session
      ) {
        setUpdatePlanPhase(UpdatePlanPhase.Authorizing)

        let confirmResult
        try {
          confirmResult = await confirmPayment(
            retryState.session.clientSecret,
            retryState.session.paymentMethodId ?? undefined,
          )
        } catch {
          setUpdatePlanPhase(UpdatePlanPhase.Idle)
          throw new Error('Payment authorization failed. Please try again.')
        }

        if (confirmResult.success) {
          // Success - clear retry state
          clearRetryData()

          setUpdatePlanPhase(UpdatePlanPhase.Confirming)
          await refetch()

          // Build success payload
          const subscriptionUpdate: SubscriptionUpdate = {
            updateType,
            planId: subscription.plan.id,
            currentPlanId: subscription.plan.id,
            channelCount: safeChannelCount,
            scheduledUpdate: null, // Immediate upgrade after SCA
          }

          // Close dialog and show success
          onOpenChange(false)
          setShowOverrideDialog(false)
          setUpdatePlanPhase(UpdatePlanPhase.Idle)
          setSubscriptionUpdateData(subscriptionUpdate)
          setShowSuccessModal(true)
          return
        } else {
          // Still failed - keep retry mode active (state unchanged)
          setUpdatePlanPhase(UpdatePlanPhase.Idle)
          throw new Error('Payment authorization failed. Please try again.')
        }
      }

      // SCENARIO B: New attempt or parameters changed (VLG-394)
      // Clear any existing retry state
      clearRetryData()

      // Set updating phase
      setUpdatePlanPhase(UpdatePlanPhase.Updating)

      const response = await updatePlan({
        plan: currentPlanId,
        interval: currentInterval,
        quantity: currentQuantity,
        // Attribute the quantity change to the originating CTA so the
        // subscription update carries marketing attribution downstream.
        // Mirrors the `cta` sent on the quantityUpdateModalViewed event.
        attribution: {
          cta: `publish-${cta}`,
          upgradePathName: 'account-upgrade',
        },
      })

      // Check if payment requires additional authentication (SCA)
      if (
        isScaEnabled &&
        response?.status === 'requiresAction' &&
        response?.paymentAction?.clientSecret
      ) {
        // Save retry session for potential retry (VLG-394)
        saveRetryData({
          clientSecret: response.paymentAction.clientSecret,
          paymentMethodId: response.paymentAction.paymentMethodId ?? null,
          planId: currentPlanId,
          interval: currentInterval,
          quantity: currentQuantity,
        })

        // Set phase to authorizing before showing Stripe modal
        setUpdatePlanPhase(UpdatePlanPhase.Authorizing)

        // Trigger Stripe 3D Secure authentication
        let confirmResult
        try {
          confirmResult = await confirmPayment(
            response.paymentAction.clientSecret,
            response.paymentAction.paymentMethodId ?? undefined,
          )
        } catch {
          // Handle unexpected errors from confirmPayment (network failures, etc.)
          setUpdatePlanPhase(UpdatePlanPhase.Idle)
          // Retry mode already enabled by saveRetryData above
          throw new Error('Payment authorization failed. Please try again.')
        }

        if (confirmResult.success) {
          // Payment confirmation succeeded - Stripe automatically updates subscription
          // Clear retry state (VLG-394)
          clearRetryData()

          // Set phase to confirming to show feedback while we refetch
          setUpdatePlanPhase(UpdatePlanPhase.Confirming)

          // Refetch to get updated subscription data
          await refetch()

          // Build success payload
          const subscriptionUpdate: SubscriptionUpdate = {
            updateType,
            planId: subscription.plan.id,
            currentPlanId: subscription.plan.id,
            channelCount: safeChannelCount,
            scheduledUpdate: null, // Immediate upgrade after SCA
          }

          // Close the main dialog first to prevent button text flash
          onOpenChange(false)
          setShowOverrideDialog(false)
          // Clear updating state and show success
          setUpdatePlanPhase(UpdatePlanPhase.Idle)
          setSubscriptionUpdateData(subscriptionUpdate)
          setShowSuccessModal(true)
          return
        } else {
          // Payment confirmation failed or was canceled
          // Retry mode already enabled by saveRetryData above
          setUpdatePlanPhase(UpdatePlanPhase.Idle)
          throw new Error('Payment authorization failed. Please try again.')
        }
      }

      // Normal flow - no SCA required
      // Close the main dialog first to prevent button text flash
      onOpenChange(false)
      setShowOverrideDialog(false)

      // Clear updating state
      setUpdatePlanPhase(UpdatePlanPhase.Idle)
      // Set the subscription update data for direct modal rendering
      setSubscriptionUpdateData({
        updateType,
        planId: subscription.plan.id,
        currentPlanId: subscription.plan.id,
        channelCount: safeChannelCount,
        scheduledUpdate: response?.scheduledUpdate || null,
      })
      setShowSuccessModal(true)
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Sorry, there was an error updating your subscription'
      toast.error(message)
      setUpdatePlanPhase(UpdatePlanPhase.Idle)
      setShowOverrideDialog(false)
    }
  }

  // Don't render if no subscription data
  if (!subscription || !queryData) {
    return <></>
  }

  if (queryData.requiresAssistedBilling) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <Dialog.Content size="medium">
          <Dialog.Header>
            <Dialog.Title>Add or Remove Channels</Dialog.Title>
          </Dialog.Header>
          <Dialog.Body>
            <AssistedBillingNotice />
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Close asChild>
              <Button variant="secondary">Close</Button>
            </Dialog.Close>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    )
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <Dialog.Content size="large">
          <Dialog.Header>
            <Dialog.Title>Add or Remove Channels</Dialog.Title>
            <Dialog.Description>
              Adjust your channel count. Changes are prorated based on your
              billing cycle.
              {isAgencyPlan && (
                <>
                  <br />
                  <br />
                  <Text as="span" size="sm" color="subtle">
                    Agency plans require a minimum of 10 channels.
                  </Text>
                </>
              )}
            </Dialog.Description>
          </Dialog.Header>

          <Dialog.Body>
            <Card className={styles.manageChannels}>
              <CounterInput
                value={safeChannelCount}
                onChange={handleCountChange}
                name="channelCount"
                min={minChannelCount}
                disabled={false}
                size="medium"
                aria-label="Number of channels"
                step={1}
                className={styles.channelSelector}
                style={getInputWidthStyle(safeChannelCount)}
                suffix={
                  <Text size="md">
                    {safeChannelCount === 1 ? 'channel' : 'channels'}
                  </Text>
                }
              />
              <Flex
                direction="column"
                gap="xs"
                align="end"
                className={styles.pricingInfo}
              >
                <Flex direction="row" gap="xs" align="center" wrap="wrap">
                  <Text size="md" weight="bold">
                    {totalDueHeader}
                  </Text>
                  <Text size="sm" color="subtle">
                    + applicable taxes
                  </Text>
                </Flex>

                {isTieredPricing && volumeDiscountMessage && (
                  <Flex direction="row" gap="2xs" align="end">
                    <Badge
                      className={styles.discountBadge}
                      variant="brand-subtle"
                    >
                      {volumeDiscountMessage}
                    </Badge>
                  </Flex>
                )}
                {discounts.length > 0 && (
                  <Flex direction="row" gap="2xs" align="end">
                    {getEntriesWithKeys(discounts, formatDiscountBadge).map(
                      ({ key, item: discount }) => {
                        const badgeText = formatDiscountBadge(discount)
                        if (!badgeText) return null
                        return (
                          <Badge
                            key={key}
                            className={styles.discountBadge}
                            variant="brand-subtle"
                          >
                            {badgeText}
                          </Badge>
                        )
                      },
                    )}
                  </Flex>
                )}
                {hasChannelCountChanged && (
                  <Skeleton
                    show={
                      isLoadingDynamicPrices || customInvoiceData === undefined
                    }
                  >
                    <Text size="sm" color="subtle">
                      {totalDueSubHeader}
                    </Text>
                  </Skeleton>
                )}
              </Flex>
            </Card>

            <Flex
              direction="row"
              gap="xs"
              align="center"
              fullWidth
              className={styles.paymentCopyWrapper}
            >
              <Text
                size="sm"
                className={styles.paymentCopy}
                icon={<CheckIcon />}
              >
                {isDowngradingAction
                  ? 'Changes take effect at your next billing date.'
                  : 'Changes take effect immediately after payment.'}
              </Text>
            </Flex>
          </Dialog.Body>

          <Dialog.Footer>
            <Dialog.Close asChild>
              <Button
                variant="secondary"
                disabled={isUpdatingPlan || isConfirming}
              >
                Cancel
              </Button>
            </Dialog.Close>

            {displayScheduledUpdate || subscription.isCanceledAtPeriodEnd ? (
              <PlanOverrideConfirmationDialog
                subscription={
                  {
                    ...subscription,
                    interval:
                      subscription.interval === BillingIntervalValues.year
                        ? BillingIntervalValues.year
                        : BillingIntervalValues.month,
                  } satisfies ExtendedPlanSelectorData['subscription']
                }
                onConfirm={handleConfirmAndPayClick}
                loading={isUpdatingPlan}
                open={showOverrideDialog}
                onOpenChange={setShowOverrideDialog}
              >
                <Button
                  variant="primary"
                  disabled={
                    !hasChannelCountChanged || isUpdatingPlan || isConfirming
                  }
                  loading={isUpdatingPlan || isConfirming}
                  onClick={() => setShowOverrideDialog(true)}
                >
                  {isUpdatingPlan || isConfirming
                    ? updatePlanPhase === UpdatePlanPhase.Updating
                      ? 'Updating plan'
                      : updatePlanPhase === UpdatePlanPhase.Authorizing
                        ? 'Authorizing payment'
                        : updatePlanPhase === UpdatePlanPhase.Confirming
                          ? 'Confirming payment'
                          : ctaButtonText
                    : ctaButtonText}
                </Button>
              </PlanOverrideConfirmationDialog>
            ) : (
              <Button
                variant="primary"
                disabled={
                  !hasChannelCountChanged || isUpdatingPlan || isConfirming
                }
                loading={isUpdatingPlan || isConfirming}
                onClick={handleConfirmAndPayClick}
              >
                {isUpdatingPlan || isConfirming
                  ? updatePlanPhase === UpdatePlanPhase.Updating
                    ? 'Updating plan'
                    : updatePlanPhase === UpdatePlanPhase.Authorizing
                      ? 'Authorizing payment'
                      : updatePlanPhase === UpdatePlanPhase.Confirming
                        ? 'Confirming payment'
                        : ctaButtonText
                  : ctaButtonText}
              </Button>
            )}
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>

      {/* Success Modal - Direct Rendering */}
      {subscriptionUpdateData && (
        <SubscriptionUpdateModal
          open={showSuccessModal}
          onOpenChange={(nextOpen) => {
            setShowSuccessModal(nextOpen)
            if (!nextOpen) {
              setSubscriptionUpdateData(null)
            }
          }}
          subscriptionUpdate={subscriptionUpdateData}
        />
      )}
    </>
  )
}
